/**
 * What the published header measures, in one place because the bar, the
 * dropdown panels and the mobile drawer all quote the same numbers. The
 * site-wide steps, frames and accent colours are in `../tokens`.
 */
import { SITE_STEPS, WIDE } from "../tokens";

/** The gutter the bar keeps once the viewport is narrower than its column. */
export const GUTTER = 16;

/** The bar's row, above the 1px rule it always carries. */
export const BAR_ROW = { desktop: 72, mobile: 53 };

/** The bar's bottom rule, which the drawer hangs off and pages clear. */
export const BAR_RULE = 1;

/** A dropdown hangs off the row; the drawer hangs off the rule below it. */
export const DRAWER_TOP = BAR_ROW.mobile + BAR_RULE;

/**
 * The published pages place their first block a fixed distance from the top of
 * the viewport and draw the fixed bar over it, so each number a page measures
 * already contains the bar's height. Ours clears the bar in flow instead, so a
 * page states what it measured and this subtracts the row it sits under.
 *
 * The keys are the site's own steps. A page whose clearance does not step where
 * the site does simply states the same number twice.
 */
export function navClearance(measured: {
  wide: number;
  lg: number;
  md: number;
  sm: number;
}) {
  const under = (value: number, row: number) => `${value - row - BAR_RULE}px`;
  return {
    pt: under(measured.sm, BAR_ROW.mobile),
    [`@media (min-width: ${SITE_STEPS.tiny}px)`]: {
      pt: under(measured.md, BAR_ROW.mobile),
    },
    [`@media (min-width: ${SITE_STEPS.card}px)`]: {
      pt: under(measured.lg, BAR_ROW.mobile),
    },
    [WIDE]: { pt: under(measured.wide, BAR_ROW.desktop) },
  };
}

/**
 * The bar sits above the published site's own overlays, and above the two
 * fixed controls this app draws on the content surfaces, the article's
 * contents bar and the academy's filter bar, which both take `OVER_BAR`.
 */
export const BAR_Z_INDEX = 9000;
export const OVER_BAR = 9999;

/** The tinted square behind a menu item's icon. */
export const ICON_TONES = {
  violet: "#F2F2FD",
  amber: "#FDEDE1",
  mint: "#E5F6E9",
  /** The untinted square, on the site's `--colors--gray-4`. */
  plain: "#F7FAFC",
};

/** Two neutrals the bar's rows carry that no palette role covers. */
export const ROW_TINT = "#F6F8F9";
export const PRICE_TINT = "#F1F4F6";

/** The compact price rows, which carry their tone on the row itself. */
export const COMPACT_TONES = {
  violet: { fill: "#F6F6FD", hover: "#6471E3" },
  mint: { fill: "#EDF7EA", hover: "#3CBA58" },
};

/**
 * The panel's curtain and its content arrive separately: the white background
 * slides down from behind the bar while each column fades up 20px, the first
 * column starting once the curtain is roughly a third of the way down.
 *
 * The published site drives all of this from JavaScript, on curves that are not
 * CSS timing functions. These are the measured envelopes, 400ms of travel,
 * columns stepping 42–75ms apart depending on the menu, fitted to the nearest
 * standard easing.
 */
export const PANEL_MOTION = {
  duration: 400,
  columnLead: 100,
  columnStep: 50,
  rise: 20,
  /**
   * Closing is not the opening reversed. The columns leave together in 125ms,
   * measured from the click, while the curtain takes the whole 400ms behind
   * them, so the panel is empty long before it has finished retracting.
   *
   * Their curve is a fit rather than a standard easing: the published exit is
   * an exponential decay, which `ease-out` trails by up to 18% mid-travel.
   */
  exit: 125,
  exitEase: "cubic-bezier(0.15, 0.3, 0.25, 0.95)",
  /** The drawer closes the same way, by height, in 200ms against its 400ms open. */
  drawerExit: 200,
};

/** The drawer's slide, and the hamburger's two-phase morph into a cross. */
export const DRAWER_MOTION = { slide: 400, converge: 275, rotate: 490 };

/**
 * A drawer row unfolding, measured on the published site at 430: the list
 * grows on the panel's exit curve while its groups wait at opacity 0, 20px
 * high, then from 235ms they fade up together over 500ms. `contentDelay` is
 * 50ms short of that because a transition's delay starts at React's commit,
 * which is what the tap costs.
 */
export const DRAWER_PANEL_MOTION = {
  curtainEase: PANEL_MOTION.exitEase,
  contentDelay: 185,
  contentDuration: 500,
  contentEase: "cubic-bezier(0.33, 1, 0.68, 1)",
};

/** A hovered menu row: its tint grows in from 110% and fades up. */
export const ROW_HOVER_MS = 200;
